/**
 * Detección de plataforma para las instrucciones de instalación.
 *
 * Función pura sobre el user agent: en este proyecto Vitest corre con
 * `environment: 'node'` (vitest.config.ts:12), sin jsdom, así que no se puede montar
 * un componente en un test unitario. Aislar la decisión acá es lo que la hace cubrible.
 */

export type Plataforma =
  /** iPhone o iPad con Safari: instalación manual vía Compartir. */
  | 'ios'
  /** Safari de escritorio en macOS: instalación real vía Archivo → Añadir al Dock. */
  | 'mac-safari'
  /** Android sin oportunidad nativa disponible: menú del navegador. */
  | 'android'
  /** Firefox en Windows desde la versión 143: instalación real vía "Web Apps" (anclar a la barra de tareas). */
  | 'firefox-windows'
  /** Firefox en Mac/Linux, o en Windows con una versión anterior a la 143: no existe instalación de apps web; se ofrece acceso directo. */
  | 'firefox-escritorio'
  /** Cualquier otro: instrucciones genéricas de menú del navegador. */
  | 'generico';

export type ClaseDeEquipo = 'telefono' | 'tablet' | 'escritorio';

export interface Entorno {
  readonly plataforma: Plataforma;
  readonly equipo: ClaseDeEquipo;
}

/**
 * @param userAgent      `navigator.userAgent`
 * @param puntosTactiles `navigator.maxTouchPoints` — necesario para distinguir un iPad
 *                       moderno de un Mac: desde iPadOS 13 el iPad se presenta como
 *                       "Macintosh" y el táctil es el único rasgo que lo delata.
 */
export function detectarPlataforma(userAgent: string, puntosTactiles = 0): Entorno {
  const esIPhone = /iPhone|iPod/i.test(userAgent);
  const esIPadClasico = /iPad/i.test(userAgent);
  const esIPadModerno = /Macintosh/i.test(userAgent) && puntosTactiles > 1;

  if (esIPhone || esIPadClasico || esIPadModerno) {
    return { plataforma: 'ios', equipo: esIPhone ? 'telefono' : 'tablet' };
  }

  if (/Android/i.test(userAgent)) {
    // Un Android sin "Mobile" en el user agent es tablet.
    return { plataforma: 'android', equipo: /Mobile/i.test(userAgent) ? 'telefono' : 'tablet' };
  }

  if (/Firefox\//i.test(userAgent)) {
    return { plataforma: esFirefoxConAnclajeWindows(userAgent) ? 'firefox-windows' : 'firefox-escritorio', equipo: 'escritorio' };
  }

  // Safari de escritorio: dice "Safari" y no es ninguno de los Chromium que también lo dicen.
  const esChromium = /Chrome|Chromium|Edg\//i.test(userAgent);
  if (/Macintosh/i.test(userAgent) && /Safari\//i.test(userAgent) && !esChromium) {
    return { plataforma: 'mac-safari', equipo: 'escritorio' };
  }

  return { plataforma: 'generico', equipo: 'escritorio' };
}

/**
 * Firefox activó "Web Apps" (anclar una pestaña a la barra de tareas de Windows,
 * `browser.taskbarTabs.enabled`) por defecto desde la versión 143, y solo en Windows —
 * no existe en Mac ni Linux. Una versión anterior no tiene el ícono en la barra de
 * direcciones, así que ofrecerle este camino sería una instrucción que no puede seguir.
 */
function esFirefoxConAnclajeWindows(userAgent: string): boolean {
  if (!/Windows/i.test(userAgent)) return false;
  const version = /Firefox\/(\d+)/i.exec(userAgent);
  return version !== null && Number(version[1]) >= 143;
}
